import { useCallback, useMemo, useEffect } from "react";
import { useRequest } from "ahooks";
import moment from "moment";
import { createForm } from "@formily/core";
import {
  Form,
  FormItem,
  Input,
  Select,
  DatePicker,
  FormButtonGroup,
  Submit,
} from "@formily/antd";
import { message, Modal } from "antd";
import { createSchemaField } from "@formily/react";
import { useParams, useNavigate } from "react-router-dom";
import {
  message as messageService,
  setting as settingService,
} from "@/services";
import { useAuthContext } from "@/components/auth/AuthProvider";
import ContentEditor from "./ContentEditor";
import AttachmentUpload from "./AttachmentUpload";
import DepartmentSelect from "./DepartmentSelect";
import PublishButton from "./PublishButton";
import styles from "./index.module.less";

const schema = {
  type: "object",
  properties: {
    type: {
      type: "stirng",
      title: "类型",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Select",
      "x-decorator-props": {
        labelWidth: 150,
      },
      "x-component-props": {
        style: { width: "200px" },
      },
      "x-reactions": "{{listMessageType}}",
    },
    sendee: {
      type: "number",
      title: "接收单位",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "DepartmentSelect",
      "x-decorator-props": {
        labelWidth: 150,
      },
      "x-component-props": {
        token: "{{{token}}}",
        style: { width: "200px" },
      },
    },
    title: {
      type: "stirng",
      title: "标题",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
      "x-decorator-props": {
        labelWidth: 150,
      },
    },
    subjects: {
      type: "stirng",
      title: "主题词",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
      "x-decorator-props": {
        labelWidth: 150,
        extra: "主题词之间用逗号隔开（中文逗号）",
      },
    },
    attachments: {
      type: "stirng",
      title: "附件",
      "x-decorator": "FormItem",
      "x-component": "AttachmentUpload",
      "x-decorator-props": {
        labelWidth: 150,
      },
      "x-component-props": {
        token: "{{{token}}}",
        accept: "image/*,.pdf,.doc,.docx",
      },
    },
    content: {
      type: "stirng",
      title: "内容",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "ContentEditor",
      "x-decorator-props": {
        labelWidth: 150,
      },
    },
    issuer: {
      type: "stirng",
      title: "签发领导",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
      "x-decorator-props": {
        labelWidth: 150,
      },
      "x-component-props": {
        style: { width: "200px" },
      },
    },
    contacts: {
      type: "stirng",
      title: "联系人",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
      "x-decorator-props": {
        labelWidth: 150,
      },
      "x-component-props": {
        style: { width: "200px" },
      },
    },
    contact_number: {
      type: "stirng",
      title: "联系电话",
      format: "phone",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "Input",
      "x-decorator-props": {
        labelWidth: 150,
      },
      "x-component-props": {
        style: { width: "200px" },
      },
    },
    published_at: {
      type: "string",
      title: "上报日期",
      required: true,
      "x-decorator": "FormItem",
      "x-component": "DatePicker",
      "x-component-props": {
        style: { width: "180px" },
        placeholder: "请选择上报日期",
        disabled: true,
        showTime: true,
      },
      "x-decorator-props": {
        labelWidth: 150,
      },
    },
  },
};

const SchemaField = createSchemaField({
  components: {
    FormItem,
    Input,
    Select,
    ContentEditor,
    DepartmentSelect,
    AttachmentUpload,
    DatePicker,
  },
});

function MessageForm() {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const form = useMemo(
    () => createForm({ validateFirst: true, messageId }),
    [messageId]
  );
  const auth = useAuthContext();
  const { loading, run } = useRequest(
    (values) => {
      const { attachments = [], subjects = [] } = values;
      if (messageId)
        return messageService.updateMessage(
          messageId,
          {
            ...values,
            attachments: attachments.map((a) => a.response.id),
            subjects: subjects.split("，").map((s) => ({ name: s })),
          },
          auth.token
        );
      else
        return messageService.createMessage(
          {
            ...values,
            attachments: attachments.map((a) => a.response.id),
            subjects: subjects.split("，").map((s) => ({ name: s })),
          },
          auth.token
        );
    },
    {
      manual: true,
      refreshDeps: [messageId],
      onSuccess: (data) => {
        if (data.status_code === 400) {
          const { status_code, ...rest } = data;
          const messages = Object.values(rest).flat();
          message.error(messages.join(", "));
        } else {
          message.success("操作成功");
          navigate("/workshop/msubm/draft");
        }
      },
    }
  );
  const listMessageType = useCallback(
    (field) => {
      settingService.listMessageType({}, auth.token).then((data) => {
        const dataSource = data.results.map((type) => ({
          label: type.name,
          value: type.id,
        }));
        field.dataSource = dataSource;
        field.loading = false;
      });
    },
    [auth]
  );

  useEffect(() => {
    Modal.warning({
      title: "严禁使用本系统上报、发布及保存涉密信息",
      okText: "我知道了",
    });
  }, []);

  useEffect(() => {
    if (!messageId) {
      form.setInitialValues({
        issuer: auth.director_name,
        contacts: auth.name,
        contact_number: auth.phone,
        published_at: moment().format("YYYY-MM-DD HH:mm:ss"),
      });
    } else {
      messageService.getMessage(messageId, auth.token).then((data) => {
        const { subjects } = data;
        form.setInitialValues({
          ...data,
          subjects: subjects.map((s) => s.name).join("，"),
        });
      });
    }
  }, [messageId, auth, form]);

  return (
    <div className={styles.messageForm}>
      <div className="message-title">上报新信息</div>
      <div className="message-body">
        <Form form={form} labelCol={4} wrapperCol={20}>
          <SchemaField
            schema={schema}
            scope={{
              token: auth.token,
              listMessageType,
            }}
          />
          <FormButtonGroup.FormItem>
            <Submit onSubmit={(values) => run(values)} loading={loading}>
              保存
            </Submit>
            <PublishButton form={form} messageId={messageId} />
          </FormButtonGroup.FormItem>
        </Form>
      </div>
    </div>
  );
}

export default MessageForm;
